"use client";

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { apiAuthFetch } from "@/lib/authFetch";
import { getAiProvider, setAiProvider } from "@/lib/storage";
import { ClientLogger } from "@/lib/clientLogger";

export type AiProviderInfo = {
  name: string;
  displayName?: string;
  type: string;
  iconPath?: string;
  modelId: string;
  isDefault: boolean;
  /** True when the resolved API key came from the server's appsettings fallback
   *  rather than a tenant-specific source. The settings panel surfaces a warning icon
   *  so the user knows this request isn't billed to a tenant-scoped key. */
  isSharedKey: boolean;
  /** Server-authoritative feature flags for this provider. Prefer these over
   *  branching on `type` — the backend is the single source of truth. */
  capabilities: { webSearch: boolean; extendedThinking: boolean };
};

type AiProviderContextValue = {
  allProviders: AiProviderInfo[];
  selectedProvider: string | null;
  setProvider: (name: string) => void;
};

const AiProviderContext = createContext<AiProviderContextValue>({
  allProviders: [],
  selectedProvider: null,
  setProvider: () => {},
});

export function AiProviderProvider({ children }: { children: ReactNode }) {
  const [allProviders, setAllProviders] = useState<AiProviderInfo[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(
    () => getAiProvider(),
  );

  // Fetch available providers on mount
  useEffect(() => {
    let cancelled = false;
    apiAuthFetch("api/providers")
      .then(res => res.ok ? res.json() : Promise.reject(res.statusText))
      .then((data: AiProviderInfo[]) => {
        if (!cancelled) setAllProviders(data);
      })
      .catch(err => {
        ClientLogger.error("Failed to fetch AI providers", {
          error: err, component: "AiProviderContext",
        });
      });
    return () => { cancelled = true; };
  }, []);

  // Re-resolve provider after async providers load, if still unresolved.
  useEffect(() => {
    if (allProviders.length === 0) return;
    if (selectedProvider !== null) return;

    const provider = allProviders.find(p => p.isDefault)?.name ?? allProviders[0]?.name ?? null;
    if (provider !== null) setSelectedProvider(provider);
  }, [allProviders, selectedProvider]);

  const setProvider = useCallback((name: string) => {
    setSelectedProvider(name);
    setAiProvider(name);
  }, []);

  return (
    <AiProviderContext.Provider value={{ allProviders, selectedProvider, setProvider }}>
      {children}
    </AiProviderContext.Provider>
  );
}

export function useAiProvider() {
  return useContext(AiProviderContext);
}
